import { giveAwayContent } from "../../../../utils/giveaAwayContent";
import { useLanguage } from "../../../../context/LanguageContext";
// components/winners/WinnerCard.jsx
export function WinnerCard({
  winner,
  onSendEmail,
  onShowDetails,
  onStatusChange,
  loading = false,
}) {
  const { language } = useLanguage();
  const t = giveAwayContent[language].winnerCard;

  const formatDate = (date) => {
    const locale = language === "de" ? "de-DE" : "en-US";
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(date));
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800",
    contacted: "bg-blue-100 text-blue-800",
    voucher_sent: "bg-orange-100 text-orange-800",
    completed: "bg-green-100 text-green-800",
  };

  // Normalisiere die Winner-Daten basierend auf der tatsächlichen Struktur
  const normalizedWinner = {
    _id: winner._id,
    participantName:
      `${winner.winner?.firstname || ""} ${
        winner.winner?.lastname || ""
      }`.trim() || t.unknownName,
    participantEmail: winner.winner?.email || t.noEmail,
    giveawayTitle: winner.giveawayTitle || t.unknownGiveaway,
    status: winner.notificationStatus || "pending",
    drawnAt: winner.createdAt, // Verwende createdAt als drawnAt
    prize: {
      title: winner.prize?.title || t.unknownPrize,
      description: winner.prize?.description || "",
      value: winner.prize?.value || t.unknownValue,
    },
    place: winner.place || 1,
    voucherCode: winner.prize?.voucherCode || "",
    voucherSent: winner.prize?.voucherCode ? true : false,
    voucherSentAt: winner.voucherSentAt || null,
    contactAttempts: winner.contactAttempts || 0,
    feedback: winner.feedback,
  };

  // ===== STATUS QUICK ACTIONS =====
  const handleQuickStatusChange = (newStatus) => {
    if (onStatusChange) {
      onStatusChange(normalizedWinner._id, newStatus);
    }
  };

  const needsAction = () => {
    // Pending status = needs initial contact or voucher sending
    if (normalizedWinner.status === "pending") {
      return {
        type: "urgent",
        message:
          t.actionRequired?.sendVoucher || "Gutschein senden erforderlich!",
        icon: "🚨",
      };
    }

    // Contacted but no voucher sent
    if (
      normalizedWinner.status === "contacted" &&
      !normalizedWinner.voucherSent
    ) {
      return {
        type: "warning",
        message:
          t.actionRequired?.voucherPending || "Gutschein noch nicht gesendet",
        icon: "⚠️",
      };
    }

    return null;
  };

  const actionAlert = needsAction();
  
  return (
    <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow">
      {actionAlert && (
        <div
          className={`mb-4 p-3 rounded-lg border-l-4 ${
            actionAlert.type === "urgent"
              ? "bg-red-50 border-red-400 text-red-700"
              : "bg-amber-50 border-amber-400 text-amber-700"
          }`}
        >
          <div className="flex items-center gap-2">
            <span>{actionAlert.icon}</span>
            <span className="font-medium text-sm">{actionAlert.message}</span>
          </div>
        </div>
      )}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            🏆 {normalizedWinner.participantName}
            {loading && (
              <span className="text-xs text-orange-500">
                {t.loadingIndicator}
              </span>
            )}
          </h3>
          <p className="text-gray-600 text-sm">
            {normalizedWinner.giveawayTitle}
          </p>
        </div>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            statusColors[normalizedWinner.status]
          }`}
        >
          {t.statusLabels[normalizedWinner.status]}
        </span>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <span>👑</span>
          <span className="ml-2">
            {normalizedWinner.prize.title} -{" "}
            {normalizedWinner.prize.description}
          </span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <span>🎁</span>
          <span className="ml-2">
            {t.details.value} {normalizedWinner.prize.value}
          </span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <span>🏅</span>
          <span className="ml-2">
            {t.details.place} {normalizedWinner.place}
          </span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <span>📅</span>
          <span className="ml-2">
            {t.details.drawn} {formatDate(normalizedWinner.drawnAt)}
          </span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <span>📧</span>
          <span className="ml-2">{normalizedWinner.participantEmail}</span>
        </div>

        {normalizedWinner.feedback && (
          <div className="flex items-center text-sm text-gray-600">
            <span>💬</span>
            <span className="ml-2">
              {t.details.feedbackSubmitted}{" "}
              {formatDate(normalizedWinner.feedback.submittedAt)}
            </span>
          </div>
        )}

        {normalizedWinner.voucherCode && (
          <div className="flex items-center text-sm text-green-600">
            <span>🎫</span>
            <span className="ml-2 font-mono bg-green-50 px-2 py-1 rounded">
              {t.details.voucherCode} {normalizedWinner.voucherCode}
            </span>
          </div>
        )}

        {normalizedWinner.voucherSentAt && (
          <div className="text-sm text-green-600 font-medium">
            {t.details.voucherSent} {formatDate(normalizedWinner.voucherSentAt)}
          </div>
        )}

        {normalizedWinner.contactAttempts > 0 && (
          <div className="text-sm text-blue-600">
            {t.details.contactAttempts} {normalizedWinner.contactAttempts}
          </div>
        )}
      </div>

      {/* Quick Status Actions */}
      {onStatusChange && (
        <div className="mb-3">
          {normalizedWinner.status === "pending" && (
            <button
              onClick={() => handleQuickStatusChange("contacted")}
              disabled={loading}
              className="w-full px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed mb-1"
            >
              {t.quickActions.markContacted}
            </button>
          )}

          {normalizedWinner.status === "contacted" && (
            <button
              onClick={() => handleQuickStatusChange("voucher_sent")}
              disabled={loading}
              className="w-full px-2 py-1 bg-orange-50 text-orange-600 rounded text-xs font-medium hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed mb-1"
            >
              {t.quickActions.markVoucherSent}
            </button>
          )}

          {normalizedWinner.status === "voucher_sent" && (
            <button
              onClick={() => handleQuickStatusChange("completed")}
              disabled={loading}
              className="w-full px-2 py-1 bg-green-50 text-green-600 rounded text-xs font-medium hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed mb-1"
            >
              {t.quickActions.markCompleted}
            </button>
          )}
        </div>
      )}

      <div className="flex gap-2">
        {!normalizedWinner.voucherSent && (
          <button
            onClick={() => onSendEmail(winner)} // Übergebe das originale winner-Objekt
            disabled={loading}
            className="flex-1 px-3 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t.buttons.sendVoucher}
          </button>
        )}
        <button
          onClick={() => onShowDetails(winner)} // Übergebe das originale winner-Objekt
          disabled={loading}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t.buttons.details}
        </button>
      </div>
    </div>
  );
}
